import React, { createContext, useContext } from 'react';

import { TestType } from '@qality/common-model';
import { IssueTestType } from '@qality/common-model/src/TestType';
import { useTestCaseRepositorySelectionContext } from '@qality/common-pages/src/TestCaseRepositoryPage/TestCaseRepositorySelectionProvider';
import {
  StorageOfUniqueServices,
  TestCasesService,
} from '@qality/common-service';

import { useTestCaseRepositoryIssueTypesContext } from '../TestCaseRepositoryIssueTypesProvider';

type TestCaseRepositoryBulkBarContextValue = {
  handleTestTypeSelect: (newTestType: TestType) => void;
};

export const initialContextValue: TestCaseRepositoryBulkBarContextValue = {
  handleTestTypeSelect: () => undefined,
};

export const TestCaseRepositoryBulkBarContext =
  createContext<TestCaseRepositoryBulkBarContextValue>(initialContextValue);

export const useTestCaseRepositoryBulkBarContext =
  (): TestCaseRepositoryBulkBarContextValue => {
    return useContext(TestCaseRepositoryBulkBarContext);
  };

type Props = {
  children: React.ReactNode;
};

export default function TestCaseRepositoryBulkBarProvider({
  children,
}: Props): JSX.Element {
  const { testCasesTestTypes, updateTestCasesTestTypes } =
    useTestCaseRepositoryIssueTypesContext();

  const { allSelectedIssuesIds } = useTestCaseRepositorySelectionContext();

  const bulkUpdateTestTypes = async (
    selectedTestCasesThatNeedTestTypeUpdate: IssueTestType[],
    newTestType: TestType
  ) => {
    const testCasesTestTypesBackup = testCasesTestTypes;
    // Set the test type of the selected test cases to null to display loading state
    updateTestCasesTestTypesContext(
      selectedTestCasesThatNeedTestTypeUpdate,
      null
    );

    try {
      await sendRequestToUpdateTestCasesTestTypes(
        selectedTestCasesThatNeedTestTypeUpdate,
        newTestType
      );
      updateTestCasesTestTypesContext(
        selectedTestCasesThatNeedTestTypeUpdate,
        newTestType
      );
    } catch (error) {
      updateTestCasesTestTypes(testCasesTestTypesBackup);
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        error,
        'Failed to update test cases test type'
      );
    }
  };

  const sendRequestToUpdateTestCasesTestTypes = async (
    selectedTestCasesThatNeedTestTypeUpdate: IssueTestType[],
    newTestType: TestType
  ) => {
    const requestData = selectedTestCasesThatNeedTestTypeUpdate.map(
      (testCaseToUpdate) => ({
        id: +testCaseToUpdate.testCaseId,
        testTypeId: newTestType.id,
      })
    );
    await TestCasesService.bulkUpdateTestCaseTestTypes(requestData);
  };

  // From the selected test cases, get the test types of the selected test cases
  const getTestTypeOfSelectedTestCases = (): IssueTestType[] => {
    return testCasesTestTypes.filter((testCaseTestType) =>
      allSelectedIssuesIds.find(
        (selectedIssueId) => testCaseTestType.testCaseId === +selectedIssueId
      )
    );
  };

  // From the selected test cases, get the test cases that don't have the selected test type and need to be updated.
  // We filter out the test cases that already have the selected test type, and don't do anything with them.
  const getTestCasesThatDontHaveSelectedTestType = (
    selectedTestCasesTestTypes: IssueTestType[],
    newTestType: TestType
  ) => {
    return selectedTestCasesTestTypes.filter(
      (testCaseTestType) => testCaseTestType.testTypeId !== newTestType.id
    );
  };

  // Function to update testCasesTestTypes context (which stores all the info about list of test cases and their test types)
  // with the new test types of the selected test cases.
  const updateTestCasesTestTypesContext = (
    selectedTestCasesThatNeedTestTypeUpdate: IssueTestType[],
    newTestType: TestType
  ) => {
    const updatedTestCasesTestTypes = testCasesTestTypes.map(
      (testCaseTestType) =>
        updateTestCaseIfTestTypeChanged(
          selectedTestCasesThatNeedTestTypeUpdate,
          testCaseTestType,
          newTestType
        )
    );
    updateTestCasesTestTypes(updatedTestCasesTestTypes);
  };

  const updateTestCaseIfTestTypeChanged = (
    selectedTestCasesThatNeedTestTypeUpdate: IssueTestType[],
    testCaseTestType: IssueTestType,
    newTestType: TestType
  ): IssueTestType => {
    const wasTestCaseIdSelected = selectedTestCasesThatNeedTestTypeUpdate.find(
      (testCase) => +testCase.testCaseId === testCaseTestType.testCaseId
    );
    if (wasTestCaseIdSelected) {
      return {
        testCaseId: +wasTestCaseIdSelected.testCaseId,
        testTypeId: newTestType?.id,
      };
    }
    return testCaseTestType;
  };

  const handleTestTypeSelect = (newTestType: TestType) => {
    const selectedTestCasesTestTypes = getTestTypeOfSelectedTestCases();
    const selectedTestCasesThatNeedTestTypeUpdate =
      getTestCasesThatDontHaveSelectedTestType(
        selectedTestCasesTestTypes,
        newTestType
      );
    if (selectedTestCasesThatNeedTestTypeUpdate.length === 0) {
      return;
    }
    bulkUpdateTestTypes(selectedTestCasesThatNeedTestTypeUpdate, newTestType);
  };

  return (
    <TestCaseRepositoryBulkBarContext.Provider
      value={{
        handleTestTypeSelect,
      }}
    >
      {children}
    </TestCaseRepositoryBulkBarContext.Provider>
  );
}
